import ringtoneUrl from '@/assets/audio/alertMessage.mp3'
import callOne from '@/assets/audio/call-1.mp3'
import callTwo from '@/assets/audio/call-2.mp3'
import callThree from '@/assets/audio/call-3.mp3'
import { useCampaign } from '@/redux/campaign'
import { toast } from '@/helpers/toast/toast.helper'
import { Campaign } from '@/models/campaign.model'
import { ChatCallPermission, ChatModel } from '@/models/chat.model'
import { MessageTemplateModel } from '@/models/message.model'
import { useChat } from '@/redux/chat'
import { setActiveChatAction, updateChatFirebaseAction } from '@/redux/chat/actions'
import { getChatById, getChatsByStatus } from '@/redux/chat/sagas/_http/chat'
import { selectCompanyOwnerId } from '@/redux/company/selectors'
import { useDepartment } from '@/redux/department'
import { selectDepartmentAction } from '@/redux/department/handles'
import { useMessages } from '@/redux/message'
import { createMessageAsync } from '@/redux/message/actions'
import { fetchMessageTemplatesAction } from '@/redux/message/handles'
import { useUser } from '@/redux/user'
import { DisconnectReason, Participant, ParticipantKind, Room, RoomEvent, Track } from 'livekit-client'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { ActiveCallCard, CallDisplayStatus, CallView, IncomingCallCard } from './VoiceCallOverlay'
import { VoiceSoundPrompt } from './VoiceSoundPrompt'
import { CallPermissionModal } from './CallPermissionModal'
import { CallPermissionValidationModal } from './CallPermissionValidationModal'
import { markCallPermissionInvitation, syncCallPermissionLookup } from './callPermissionChatApi'
import { effectiveCallPermissionStatus } from './callPermissionState'
import { normalizedVoiceContactName } from './contactName'
import { officialCallAvailability } from './officialCallAvailability'
import {
  META_CONNECT_TIMEOUT_MS,
  META_MAX_CONNECT_ATTEMPTS,
  ProviderConnectionError,
  retryableSipFailure,
  sipFailureReason,
  waitBeforeRetry
} from './metaCallResilience'
import { RemoteAudioMeter } from './RemoteAudioMeter'
import { WebPcmMedia } from './WebPcmMedia'
import {
  officialVoiceApi,
  OperatorCall,
  subscribeOperatorCalls,
  VoiceChannel,
  VoiceDirection,
  VoiceSession,
  webVoiceApi
} from './voiceApi'

const LEADER_KEY = 'devzapp.voice.leader.v1'
const LEADER_TTL_MS = 8_000

class OperatorCallCancelledError extends Error {
  constructor() {
    super('Operator cancelled the call')
    this.name = 'OperatorCallCancelledError'
  }
}

export type OutboundCall = {
  phone: string
  name?: string
  photo?: string
  campaignId: string
  channel: VoiceChannel
  chatId?: string
  ownerId?: string
  callPermission?: ChatCallPermission
  stageName?: string
  context?: Record<string, unknown>
}

type CallState = CallView & {
  id: string
  providerCallId?: string
  runId?: string
  campaignId: string
  chatId?: string
  startedAt: number
  connectedAt?: number
  status: CallDisplayStatus
}

type IncomingState = Omit<CallState, 'startedAt' | 'status'>

type IncomingQueueItem = IncomingState & { ringingAt: number }

function callTicket(call: IncomingState | OutboundCall) {
  return 'id' in call ? call.ticket : String(call.context?.ticket || '') || undefined
}

type VoiceContextValue = {
  activeCall: CallState | null
  waitingCalls: IncomingQueueItem[]
  busy: boolean
  elapsed: string
  muted: boolean
  speaking: boolean
  remoteAudioLevel: number
  startOutboundCall: (call: OutboundCall) => Promise<void>
  openCallInvitation: (call: OutboundCall) => Promise<void>
  hangup: () => Promise<void>
  toggleMute: () => Promise<void>
  openActiveChat: () => void
  acceptWaitingCall: (callId: string) => Promise<void>
  rejectWaitingCall: (callId: string) => Promise<void>
  openWaitingChat: (callId: string) => void
}

const VoiceContext = createContext<VoiceContextValue | null>(null)

function digits(value: unknown) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, 18)
}

function isCallTemplate(template: MessageTemplateModel) {
  return String(template.typeSent || '')
    .toUpperCase()
    .includes('CALL_PERMISSION_REQUEST')
}

function sameBrazilianPhone(first: unknown, second: unknown) {
  const left = digits(first)
  const right = digits(second)
  if (left === right) return true
  if (!/^55\d{10,11}$/.test(left) || !/^55\d{10,11}$/.test(right)) return false

  const withoutMobileDigit = (phone: string) =>
    phone.length === 13 && phone[4] === '9' ? `${phone.slice(0, 4)}${phone.slice(5)}` : phone
  return withoutMobileDigit(left) === withoutMobileDigit(right)
}

function linkedChat(call: Pick<CallView, 'phone'> & { campaignId?: string; chatId?: string }, chats: ChatModel[]) {
  return (
    chats.find(
      chat =>
        sameBrazilianPhone(chat.requesterNumber, call.phone) &&
        (!call.campaignId || chat.campaignId === call.campaignId)
    ) ||
    chats.find(chat => call.chatId && chat.id === call.chatId) ||
    (!call.campaignId ? chats.find(chat => sameBrazilianPhone(chat.requesterNumber, call.phone)) : undefined)
  )
}

function enrichCallName<T extends CallView & { campaignId?: string; chatId?: string }>(
  call: T,
  chats: ChatModel[],
  openChatId?: string
) {
  const chat = linkedChat(call, chats)
  const currentName = normalizedVoiceContactName(call.name, call.phone)
  const name = currentName || normalizedVoiceContactName(chat?.chatName, call.phone)
  const callPermission = chat?.callPermission ?? call.callPermission
  const chatIsOpen = Boolean(openChatId && (chat?.id === openChatId || call.chatId === openChatId))
  if (name === call.name && callPermission === call.callPermission && chatIsOpen === call.chatIsOpen) return call
  return { ...call, name, callPermission, chatIsOpen }
}

function mapIncoming(call: OperatorCall, chats: ChatModel[]): IncomingQueueItem {
  const context = call.contactContext || {}
  const channel: VoiceChannel = call.channel === 'whatsapp_web' ? 'web' : 'meta'
  const phone = digits(call.phone)
  const campaignId = call.agentId || String(context.campaignId || '')
  const chatId = String(context.chatId || '') || undefined
  const chat = linkedChat({ phone, campaignId, chatId }, chats)
  return {
    id: call.id,
    providerCallId: call.providerCallId,
    runId: call.id,
    campaignId,
    phone,
    name:
      normalizedVoiceContactName(call.profileName, phone) ||
      normalizedVoiceContactName(context.contactName, phone) ||
      normalizedVoiceContactName(chat?.chatName, phone),
    photo: call.profilePicUrl || chat?.requesterPhoto,
    channel,
    direction: (String(call.direction).toUpperCase() === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND') as VoiceDirection,
    chatId: chatId || chat?.id,
    stageName: String(context.currentStageName || context.stageName || '') || undefined,
    ticket: String(context.ticket || '') || undefined,
    callPermission: chat?.callPermission,
    ringingAt: call.startedAt ? new Date(call.startedAt).getTime() : Date.now()
  }
}

function sortIncoming(calls: IncomingQueueItem[]) {
  return [...calls].sort((first, second) => first.ringingAt - second.ringingAt || first.id.localeCompare(second.id))
}

function reconcileIncoming(calls: IncomingQueueItem[]) {
  return sortIncoming(Array.from(new Map(calls.map(call => [call.id, call])).values()))
}

function upsertIncoming(current: IncomingQueueItem[], call: IncomingQueueItem) {
  return reconcileIncoming([...current.filter(item => item.id !== call.id), call])
}

function selectedCompanyId(searchParams: URLSearchParams) {
  return searchParams.get('companyId') || localStorage.getItem('devchat_app_selectedCompanyId') || ''
}

function useElapsed(startedAt?: number) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!startedAt) return
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [startedAt])
  const seconds = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function socketUrl(path: string) {
  if (/^wss?:\/\//i.test(path)) return path
  if (/^https?:\/\//i.test(path)) return path.replace(/^http/i, 'ws')
  const configured = process.env.REACT_APP_WEB_CALLS_WS_ORIGIN
  const origin = configured || window.location.origin
  return `${origin.replace(/^http/, 'ws').replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`
}

export function VoiceCallProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const companyId = selectedCompanyId(searchParams)
  const navigate = useNavigate()
  const location = useLocation()
  const { campaignState } = useCampaign()
  const { chatState, chatDispatch } = useChat()
  const {
    userState: { userAuthenticated }
  } = useUser()
  const {
    departmentState: { selectedDepartment },
    departmentDispatch
  } = useDepartment()
  const companyOwnerId = useSelector(selectCompanyOwnerId)
  const {
    messageState: { messageTemplates, isLoadingOnFetchMessageTemplates },
    messagesDispatch
  } = useMessages()
  const campaigns = campaignState.campaignsByAttend || []
  const chats = useMemo(
    () =>
      [
        chatState.activeChat,
        ...(chatState.openChats || []),
        ...(chatState.inProgressChats || []),
        ...(chatState.finishedChats || []),
        ...(chatState.chats || [])
      ].filter(chat => chat?.id),
    [chatState.activeChat, chatState.chats, chatState.finishedChats, chatState.inProgressChats, chatState.openChats]
  )
  const chatsRef = useRef<ChatModel[]>(chats)
  chatsRef.current = chats
  const hasOpenChat = location.pathname.includes('/chat-page') && Boolean(chatState.activeChat?.id)
  const tabId = useRef(crypto.randomUUID())
  const [leader, setLeader] = useState(false)
  const [incomingCalls, setIncomingCalls] = useState<IncomingQueueItem[]>([])
  const [officialRingtones, setOfficialRingtones] = useState<Record<string, string>>({})
  const [officialRingtonesLoaded, setOfficialRingtonesLoaded] = useState(false)
  const [ringtoneError, setRingtoneError] = useState<'blocked' | 'failed' | null>(null)
  const [soundPromptDone, setSoundPromptDone] = useState(false)
  const ringtoneRef = useRef<HTMLAudioElement | null>(null)
  const soundTestTimerRef = useRef<number | null>(null)
  const soundTestGenerationRef = useRef(0)
  const [activeCall, setActiveCall] = useState<CallState | null>(null)
  const [busy, setBusy] = useState(false)
  const [muted, setMuted] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [remoteAudioLevel, setRemoteAudioLevel] = useState(0)
  const [permissionTarget, setPermissionTarget] = useState<{
    phone: string
    campaignId: string
    contactName?: string
    chatId?: string
    ownerId?: string
  } | null>(null)
  const [permissionTemplateId, setPermissionTemplateId] = useState('')
  const [permissionTemplateSearch, setPermissionTemplateSearch] = useState('')
  const [permissionValidation, setPermissionValidation] = useState<'checking' | 'denied' | null>(null)
  const activeRef = useRef<CallState | null>(null)
  const incomingCallsRef = useRef<IncomingQueueItem[]>([])
  const operatorClaimsRef = useRef(
    new Map<string, { action: 'accept' | 'reject'; actor?: { id?: string; name?: string; email?: string } }>()
  )
  const autoOpenedIncomingRef = useRef(new Set<string>())
  const callActionRef = useRef(false)
  const outboundOperationRef = useRef<AbortController | null>(null)
  const locallyTerminatedRunRef = useRef<string | null>(null)
  const roomRef = useRef<Room | null>(null)
  const pcmRef = useRef<WebPcmMedia | null>(null)
  const microphoneRef = useRef<MediaStream | null>(null)
  const remoteMeterRef = useRef<RemoteAudioMeter | null>(null)
  const elapsed = useElapsed(activeCall?.connectedAt || activeCall?.startedAt)
  const openChatId = hasOpenChat ? chatState.activeChat?.id : undefined
  // The server only publishes calls it has already accepted as RINGING for this
  // tenant. A separate Meta/LiveKit readiness audit can lag or fail while the
  // call is live, so it must not hide the answer/reject UI or its ringtone.
  const incomingViews = useMemo(
    () => incomingCalls.map(call => enrichCallName(call, chats, openChatId)),
    [chats, incomingCalls, openChatId]
  )
  const incomingView = activeCall ? null : incomingViews[0] || null
  const officialIncomingCampaignIds = [
    ...new Set(incomingCalls.filter(call => call.channel === 'meta').map(call => call.campaignId))
  ]
    .sort()
    .join(',')
  const waitingCallViews = activeCall ? incomingViews : incomingViews.slice(1)
  const activeCallView = useMemo(
    () => (activeCall ? enrichCallName(activeCall, chats, openChatId) : null),
    [activeCall, chats, openChatId]
  )
  const permissionTemplates = useMemo(
    () =>
      messageTemplates.filter(
        template => template.campaignId === permissionTarget?.campaignId && isCallTemplate(template)
      ),
    [messageTemplates, permissionTarget?.campaignId]
  )
  const selectedPermissionTemplate = useMemo(
    () => permissionTemplates.find(template => template.id === permissionTemplateId) || null,
    [permissionTemplateId, permissionTemplates]
  )

  useEffect(() => {
    activeRef.current = activeCall
  }, [activeCall])

  useEffect(() => {
    incomingCallsRef.current = incomingCalls
  }, [incomingCalls])

  useEffect(() => {
    if (!permissionTarget?.campaignId) return
    setPermissionTemplateId('')
    setPermissionTemplateSearch('')
    messagesDispatch(fetchMessageTemplatesAction.request({ campaignId: permissionTarget.campaignId }))
  }, [messagesDispatch, permissionTarget?.campaignId])

  useEffect(() => {
    if (!leader || !companyId) return
    const webCampaigns = campaigns.filter(
      campaign => !campaign.isOfficial && (!campaign.companyId || campaign.companyId === companyId)
    )
    Promise.allSettled(webCampaigns.map(async campaign => webVoiceApi.register(companyId, campaign.id))).catch(
      () => undefined
    )
  }, [campaigns, companyId, leader])

  useEffect(() => {
    if (!companyId) {
      setOfficialRingtones({})
      setOfficialRingtonesLoaded(false)
      return
    }
    let current = true
    setOfficialRingtonesLoaded(false)
    void officialVoiceApi
      .listConfigs(companyId)
      .then(({ items }) => {
        if (!current) return
        setOfficialRingtones(
          Object.fromEntries(
            items
              .filter(item => item.provider === 'META_OFFICIAL' && item.agentId)
              .map(item => [item.agentId, item.ringtone || 'CALL_1'])
          )
        )
      })
      .catch(() => {
        if (current) setOfficialRingtones({})
      })
      .finally(() => {
        if (current) setOfficialRingtonesLoaded(true)
      })
    return () => {
      current = false
    }
  }, [companyId, officialIncomingCampaignIds])

  useEffect(() => {
    // A previous page load does not prove that a new Audio element may autoplay.
    // Ask once per mounted operator/company session, before the first call.
    setSoundPromptDone(false)
  }, [companyId, userAuthenticated?.id])
  const soundPromptTone = Object.entries(officialRingtones).find(
    ([campaignId, tone]) =>
      tone !== 'OFF' && campaigns.some(campaign => campaign.id === campaignId && campaign.isOfficial)
  )?.[1]
  const showSoundPrompt = Boolean(
    leader &&
      companyId &&
      userAuthenticated?.id &&
      userAuthenticated?.isWork &&
      officialRingtonesLoaded &&
      soundPromptTone &&
      !soundPromptDone &&
      !incomingView &&
      !activeCall
  )

  const activateSound = useCallback(() => {
    if (!soundPromptTone) return
    const generation = ++soundTestGenerationRef.current
    const source = soundPromptTone === 'CALL_2' ? callTwo : soundPromptTone === 'CALL_3' ? callThree : callOne
    const ringtone = ringtoneRef.current || new Audio()
    ringtoneRef.current = ringtone
    ringtone.pause()
    ringtone.src = source
    ringtone.loop = false
    ringtone.volume = 0.4
    void ringtone.play().then(
      () => {
        if (generation !== soundTestGenerationRef.current) return
        setSoundPromptDone(true)
        if (soundTestTimerRef.current) window.clearTimeout(soundTestTimerRef.current)
        soundTestTimerRef.current = window.setTimeout(() => {
          ringtone.pause()
          ringtone.currentTime = 0
        }, 1_500)
      },
      (error: unknown) => {
        if (generation !== soundTestGenerationRef.current) return
        const reason = error instanceof DOMException && error.name === 'NotAllowedError' ? 'blocked' : 'failed'
        setRingtoneError(reason)
        toast({ type: 'error', message: t(`translation:voice.ringtone.${reason}`) })
      }
    )
  }, [soundPromptTone, t])

  useEffect(
    () => () => {
      if (soundTestTimerRef.current) window.clearTimeout(soundTestTimerRef.current)
    },
    []
  )

  useEffect(() => {
    if (!activeCall) return
    soundTestGenerationRef.current++
    if (soundTestTimerRef.current) window.clearTimeout(soundTestTimerRef.current)
    ringtoneRef.current?.pause()
  }, [activeCall?.id])

  const dismissSoundPrompt = useCallback(() => {
    setSoundPromptDone(true)
  }, [])

  useEffect(() => {
    if (!companyId) {
      setLeader(false)
      return
    }
    // A lease shared by every company lets a tab for another tenant consume
    // this tenant's call stream. A visible tab takes over a hidden leader,
    // but a lone hidden tab keeps listening for calls in the background.
    const leaderKey = `${LEADER_KEY}.${companyId}`
    const claim = () => {
      const now = Date.now()
      const visible = document.visibilityState === 'visible'
      let current: { id: string; expiresAt: number; visible?: boolean } | null = null
      try {
        current = JSON.parse(localStorage.getItem(leaderKey) || 'null')
      } catch {
        current = null
      }
      if (!current || current.expiresAt < now || current.id === tabId.current || (visible && !current.visible)) {
        localStorage.setItem(leaderKey, JSON.stringify({ id: tabId.current, expiresAt: now + LEADER_TTL_MS, visible }))
      }
      try {
        setLeader(JSON.parse(localStorage.getItem(leaderKey) || 'null')?.id === tabId.current)
      } catch {
        setLeader(false)
      }
    }
    claim()
    const timer = window.setInterval(claim, 3_000)
    document.addEventListener('visibilitychange', claim)
    window.addEventListener('focus', claim)
    window.addEventListener('storage', claim)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', claim)
      window.removeEventListener('focus', claim)
      window.removeEventListener('storage', claim)
      try {
        const current = JSON.parse(localStorage.getItem(leaderKey) || 'null')
        if (current?.id === tabId.current) localStorage.removeItem(leaderKey)
      } catch {
        // A malformed lease will expire or be replaced by the next visible tab.
      }
    }
  }, [companyId])

  useEffect(() => {
    setRingtoneError(null)
    if (!incomingView || activeCall) return
    if (incomingView.channel === 'meta' && !officialRingtonesLoaded) return
    soundTestGenerationRef.current++
    const selected = incomingView.channel === 'meta' ? officialRingtones[incomingView.campaignId] || 'CALL_1' : 'WEB'
    if (selected === 'OFF') return
    const source =
      selected === 'CALL_2'
        ? callTwo
        : selected === 'CALL_3'
        ? callThree
        : selected === 'CALL_1'
        ? callOne
        : ringtoneUrl
    if (soundTestTimerRef.current) window.clearTimeout(soundTestTimerRef.current)
    const ringtone = ringtoneRef.current || new Audio()
    ringtoneRef.current = ringtone
    ringtone.pause()
    ringtone.src = source
    ringtone.loop = true
    ringtone.volume = incomingView.channel === 'meta' ? 0.4 : 0.7
    let current = true
    const reportFailure = (error: unknown) => {
      if (!current) return
      const reason = error instanceof DOMException && error.name === 'NotAllowedError' ? 'blocked' : 'failed'
      console.warn('Incoming call ringtone could not play', { reason, tone: selected, error })
      setRingtoneError(reason)
    }
    void ringtone.play().then(() => {
      if (current) setRingtoneError(null)
    }, reportFailure)
    return () => {
      current = false
      ringtone.pause()
      ringtone.currentTime = 0
    }
  }, [
    activeCall,
    incomingView?.id,
    incomingView?.campaignId,
    incomingView?.channel,
    officialRingtones,
    officialRingtonesLoaded
  ])

  const retryRingtone = useCallback(() => {
    const ringtone = ringtoneRef.current
    if (!ringtone) return
    // Invoked directly from the operator's click, while browser user activation is available.
    void ringtone.play().then(
      () => setRingtoneError(null),
      (error: unknown) => {
        const reason = error instanceof DOMException && error.name === 'NotAllowedError' ? 'blocked' : 'failed'
        console.warn('Incoming call ringtone retry failed', { reason, error })
        setRingtoneError(reason)
      }
    )
  }, [])

  const closeMedia = useCallback(async () => {
    setSpeaking(false)
    setRemoteAudioLevel(0)
    remoteMeterRef.current?.stop()
    remoteMeterRef.current = null
    microphoneRef.current?.getTracks().forEach(track => track.stop())
    microphoneRef.current = null
    const room = roomRef.current
    roomRef.current = null
    room?.disconnect()
    const pcm = pcmRef.current
    pcmRef.current = null
    await pcm?.close().catch(() => undefined)
    document.querySelectorAll('[data-devzapp-voice-audio]').forEach(node => node.remove())
    setMuted(false)
  }, [])

  const terminateRemoteCall = useCallback(
    async (call: CallState) => {
      if (!companyId) return
      if (call.channel === 'meta' && call.runId) {
        const connected = call.status === 'connected' || Boolean(call.connectedAt)
        await officialVoiceApi.terminate(
          companyId,
          call.runId,
          connected ? 'OPERATOR_HANGUP' : 'OPERATOR_CANCELLED',
          call.connectedAt
        )
      } else if (call.channel === 'web') {
        await webVoiceApi.action(companyId, call.campaignId, 'terminate', call.id)
      }
    },
    [companyId]
  )

  const hangup = useCallback(async () => {
    const call = activeRef.current
    outboundOperationRef.current?.abort()
    outboundOperationRef.current = null
    locallyTerminatedRunRef.current = call?.runId || null
    activeRef.current = null
    setActiveCall(null)
    setBusy(false)
    await closeMedia()
    if (!call) return
    await terminateRemoteCall(call).catch(() => undefined)
  }, [closeMedia, terminateRemoteCall])

  useEffect(
    () => () => {
      outboundOperationRef.current?.abort()
      outboundOperationRef.current = null
      closeMedia().catch(() => undefined)
    },
    [closeMedia, t]
  )

  const connectLiveKit = useCallback(
    async (
      session: VoiceSession,
      call: IncomingState | OutboundCall,
      waitForProviderActive = false,
      signal?: AbortSignal
    ) => {
      if (signal?.aborted) throw new OperatorCallCancelledError()
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          channelCount: 1,
          sampleRate: 48_000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      roomRef.current = room
      const isCurrentRoom = () => roomRef.current === room
      let providerActive = false
      let providerConnectedAt: number | undefined
      let providerReadySettled = false
      let resolveProviderReady: (() => void) | undefined
      let rejectProviderReady: ((error: Error) => void) | undefined
      const providerReady = waitForProviderActive
        ? new Promise<void>((resolve, reject) => {
            resolveProviderReady = resolve
            rejectProviderReady = reject
          })
        : Promise.resolve()
      // Participant callbacks can reject before execution reaches the await.
      // Observe immediately while preserving the rejection for the later await.
      void providerReady.catch(() => undefined)
      const settleProviderReady = (error?: Error) => {
        if (!waitForProviderActive || providerReadySettled) return
        providerReadySettled = true
        if (error) rejectProviderReady?.(error)
        else resolveProviderReady?.()
      }
      const cancelProviderWait = () => settleProviderReady(new OperatorCallCancelledError())
      if (waitForProviderActive) {
        if (signal?.aborted) cancelProviderWait()
        else signal?.addEventListener('abort', cancelProviderWait, { once: true })
      }
      const finishRemoteCall = () => {
        if (!isCurrentRoom()) return
        setActiveCall(null)
        closeMedia().catch(() => undefined)
        if (companyId && session.runId) {
          officialVoiceApi
            .terminate(companyId, session.runId, 'REMOTE_HANGUP', providerConnectedAt)
            .catch(() => undefined)
        }
      }
      const isRemote = (participant: Participant) =>
        participant.identity === session.participantIdentity?.trim() ||
        participant.kind === ParticipantKind.SIP ||
        participant.kind === ParticipantKind.CONNECTOR ||
        participant.identity === `meta-${session.providerCallId}`
      const providerFailure = (statusCode?: string, fallback?: string) => {
        const reason = sipFailureReason(statusCode)
        const message =
          reason === 'declined'
            ? t('translation:voice.callDeclined')
            : reason === 'unavailable'
            ? t('translation:voice.contactUnavailable')
            : statusCode
            ? `SIP ${statusCode}`
            : fallback || t('translation:voice.providerEndedBeforeAnswer')
        return new ProviderConnectionError(message, retryableSipFailure(statusCode), statusCode, reason)
      }
      const updateStatus = (participant: Participant) => {
        if (!isCurrentRoom() || !isRemote(participant)) return
        const status = participant.attributes['sip.callStatus']
        if (status === 'active') {
          if (!providerActive && companyId && session.runId) {
            officialVoiceApi.markActive(companyId, session.runId).catch(() => undefined)
          }
          providerActive = true
          providerConnectedAt ||= Date.now()
          settleProviderReady()
          setActiveCall(current =>
            current
              ? { ...current, status: 'connected', connectedAt: current.connectedAt || providerConnectedAt }
              : current
          )
        } else if (status === 'hangup') {
          const statusCode = participant.attributes['sip.callStatusCode']
          if (!providerActive) {
            settleProviderReady(providerFailure(statusCode))
            return
          }
          finishRemoteCall()
        }
      }
      room.on(RoomEvent.ActiveSpeakersChanged, participants => {
        if (isCurrentRoom()) setSpeaking(participants.some(isRemote))
      })
      room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        if (!isCurrentRoom()) return
        if (track.kind !== Track.Kind.Audio) return
        const element = track.attach()
        element.autoplay = true
        element.dataset.devzappVoiceAudio = 'true'
        document.body.appendChild(element)
        element.play().catch(() => undefined)
        if (isRemote(participant) && participant.kind === ParticipantKind.CONNECTOR) {
          if (!providerActive && companyId && session.runId) {
            officialVoiceApi.markActive(companyId, session.runId).catch(() => undefined)
          }
          providerActive = true
          providerConnectedAt ||= Date.now()
          settleProviderReady()
          setActiveCall(current =>
            current
              ? { ...current, status: 'connected', connectedAt: current.connectedAt || providerConnectedAt }
              : current
          )
        }
      })
      room.on(RoomEvent.TrackUnsubscribed, track => track.detach().forEach(element => element.remove()))
      room.on(RoomEvent.ParticipantConnected, updateStatus)
      room.on(RoomEvent.ParticipantAttributesChanged, (_attributes, participant) => updateStatus(participant))
      room.on(RoomEvent.ParticipantDisconnected, participant => {
        if (!isCurrentRoom() || !isRemote(participant)) return
        if (!providerActive) {
          const statusCode = participant.attributes['sip.callStatusCode']
          settleProviderReady(providerFailure(statusCode))
          return
        }
        finishRemoteCall()
      })
      room.on(RoomEvent.Disconnected, reason => {
        if (!isCurrentRoom()) return
        if (!providerActive) {
          if (reason === DisconnectReason.USER_REJECTED) {
            settleProviderReady(
              new ProviderConnectionError(t('translation:voice.callDeclined'), false, undefined, 'declined')
            )
          } else if (reason === DisconnectReason.USER_UNAVAILABLE) {
            settleProviderReady(
              new ProviderConnectionError(t('translation:voice.contactUnavailable'), false, undefined, 'unavailable')
            )
          } else {
            settleProviderReady(new ProviderConnectionError(t('translation:voice.mediaDisconnected'), true))
          }
        }
        if (providerActive && activeRef.current) finishRemoteCall()
        else closeMedia().catch(() => undefined)
      })
      room.on(RoomEvent.Reconnecting, () => {
        if (isCurrentRoom()) setActiveCall(current => (current ? { ...current, status: 'stabilizing' } : current))
      })
      room.on(RoomEvent.Reconnected, () => {
        if (isCurrentRoom() && providerActive) {
          setActiveCall(current => (current ? { ...current, status: 'connected' } : current))
        }
      })
      const base = 'id' in call ? call : { ...call, id: session.providerCallId || session.runId }
      setActiveCall(current => ({
        ...base,
        id: session.providerCallId || ('id' in call ? call.id : session.runId),
        runId: session.runId,
        name: normalizedVoiceContactName(call.name, call.phone),
        direction: 'direction' in call ? call.direction : 'OUTBOUND',
        ticket: callTicket(call),
        startedAt: current?.startedAt || Date.now(),
        status: 'preparing'
      }))
      try {
        await room.connect(session.livekitUrl, session.accessToken)
        await room.startAudio()
        await room.localParticipant.setMicrophoneEnabled(true)
        setActiveCall(current =>
          current && current.status !== 'connected' ? { ...current, status: 'calling' } : current
        )
        room.remoteParticipants.forEach(updateStatus)
        try {
          const meter = new RemoteAudioMeter(room, isRemote, (remoteSpeaking, level) => {
            if (!isCurrentRoom()) return
            setSpeaking(remoteSpeaking)
            setRemoteAudioLevel(level)
          })
          remoteMeterRef.current?.stop()
          remoteMeterRef.current = meter
          meter.start()
        } catch {
          // The call must remain usable even if Web Audio metering is unavailable.
          setRemoteAudioLevel(0)
        }
        if (waitForProviderActive) {
          const timeout = window.setTimeout(
            () => settleProviderReady(new ProviderConnectionError('Tempo esgotado aguardando o atendimento.', true)),
            META_CONNECT_TIMEOUT_MS
          )
          try {
            await providerReady
          } finally {
            window.clearTimeout(timeout)
            signal?.removeEventListener('abort', cancelProviderWait)
          }
        }
      } catch (error) {
        signal?.removeEventListener('abort', cancelProviderWait)
        if (error instanceof OperatorCallCancelledError || signal?.aborted) {
          throw new OperatorCallCancelledError()
        }
        if (!waitForProviderActive || error instanceof ProviderConnectionError) throw error
        const permissionDenied =
          error instanceof DOMException && ['NotAllowedError', 'SecurityError'].includes(error.name)
        throw new ProviderConnectionError(
          error instanceof Error ? error.message : 'Não foi possível estabelecer a mídia da ligação.',
          !permissionDenied,
          undefined,
          'connection'
        )
      }
    },
    [closeMedia, companyId, t]
  )

  const connectWeb = useCallback(
    async (call: IncomingState | OutboundCall, incomingCall: boolean) => {
      if (!companyId) throw new Error(t('translation:voice.companyRequired'))
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48_000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      microphoneRef.current = microphone
      const media = new WebPcmMedia()
      pcmRef.current = media
      media.setRemoteActivityListener((remoteSpeaking, level) => {
        setSpeaking(remoteSpeaking)
        setRemoteAudioLevel(level)
      })
      await media.setInputStream(microphone)
      let callId: string
      if (incomingCall && 'id' in call) {
        await officialVoiceApi.claim(companyId, call.runId || call.id, 'accept')
        callId = call.providerCallId || call.id
        await webVoiceApi.action(companyId, call.campaignId, 'accept', callId)
      } else {
        const response = await webVoiceApi.start(companyId, call.campaignId, call.phone)
        callId = response.results.callId
      }
      const ticket = await webVoiceApi.ticket(companyId, call.campaignId, callId)
      await media.connect(socketUrl(ticket.results.streamPath), () => {
        setActiveCall(null)
        closeMedia().catch(() => undefined)
      })
      setActiveCall({
        ...call,
        id: callId,
        name: normalizedVoiceContactName(call.name, call.phone),
        direction: 'direction' in call ? call.direction : 'OUTBOUND',
        ticket: callTicket(call),
        startedAt: Date.now(),
        status: 'calling'
      })
      media
        .waitUntilProviderReady()
        .then(() => {
          setActiveCall(current =>
            current?.id === callId ? { ...current, status: 'connected', connectedAt: Date.now() } : current
          )
        })
        .catch(() => {
          hangup().catch(() => undefined)
        })
    },
    [closeMedia, companyId, hangup, t]
  )

  const findExistingChat = useCallback(
    async (call: IncomingState | CallState) => {
      const loadedChat = linkedChat(call, chatsRef.current)
      if (loadedChat) return loadedChat
      if (!companyOwnerId) return null

      if (call.chatId) {
        try {
          const { chat } = await getChatById(call.chatId, companyOwnerId)
          if (chat) return chat
        } catch {
          // Some providers do not send a valid chat id. Fall back to the phone lookup below.
        }
      }

      const accessibleDepartments = (userAuthenticated?.departments || []).filter(
        department => department.companyId === companyId
      )
      const results = await Promise.allSettled(
        accessibleDepartments.map(async department =>
          getChatsByStatus({
            ownerId: companyOwnerId,
            campaignIds: [call.campaignId],
            status: ['open', 'in_progress', 'close'],
            departmentId: department.id,
            queryLimit: 5,
            chatFilters: { requesterNumber: call.phone }
          })
        )
      )
      return (
        results
          .flatMap(result => (result.status === 'fulfilled' ? result.value.chats : []))
          .find(chat => sameBrazilianPhone(chat.requesterNumber, call.phone)) || null
      )
    },
    [companyId, companyOwnerId, userAuthenticated?.departments]
  )

  const openChat = useCallback(
    async (call: IncomingState | CallState) => {
      const chat = await findExistingChat(call)
      if (!chat) return false

      const targetDepartment = (userAuthenticated?.departments || []).find(
        department => department.id === chat.departmentId || department.id === chat.department?.id
      )
      const alreadyInDepartment =
        selectedDepartment?.id === chat.departmentId || selectedDepartment?.id === chat.department?.id
      if (!alreadyInDepartment) {
        if (!targetDepartment) return false
        localStorage.setItem('devchat_app_selectedDepartment', JSON.stringify(targetDepartment))
        departmentDispatch?.(
          selectDepartmentAction.request({
            selectedDepartment: targetDepartment,
            returnOldDepartment: true,
            userAuthenticated
          })
        )
      }

      chatDispatch(setActiveChatAction.request({ activeChat: { ...chat, unreadMessagesCount: 0 } }))
      navigate(`/chat-page?${new URLSearchParams({ companyId, chatId: chat.id })}`)
      return true
    },
    [
      chatDispatch,
      companyId,
      departmentDispatch,
      findExistingChat,
      navigate,
      selectedDepartment?.id,
      userAuthenticated,
      userAuthenticated?.departments
    ]
  )

  useEffect(() => {
    const incoming = incomingViews[0]
    if (!incoming || activeCall || autoOpenedIncomingRef.current.has(incoming.id)) return

    autoOpenedIncomingRef.current.add(incoming.id)
    void openChat(incoming).then(opened => {
      if (!opened) autoOpenedIncomingRef.current.delete(incoming.id)
    })
  }, [activeCall, incomingViews, openChat])

  const acceptIncoming = useCallback(
    async (callId: string) => {
      const call = incomingCallsRef.current.find(item => item.id === callId)
      if (!call || busy || callActionRef.current || !companyId) return
      callActionRef.current = true
      const previousCall = activeRef.current
      setIncomingCalls(current => current.filter(item => item.id !== call.id))
      setActiveCall({
        ...call,
        startedAt: Date.now(),
        status: 'preparing'
      })
      setBusy(true)
      try {
        await closeMedia()
        const previousTermination = previousCall
          ? terminateRemoteCall(previousCall).catch(() => undefined)
          : Promise.resolve()
        if (call.channel === 'meta')
          await connectLiveKit(await officialVoiceApi.accept(companyId, call.runId || call.id), call)
        else await connectWeb(call, true)
        await previousTermination
        await openChat(call)
      } catch (error) {
        setActiveCall(null)
        await closeMedia()
        toast({ type: 'error', message: error instanceof Error ? error.message : t('translation:voice.unavailable') })
      } finally {
        callActionRef.current = false
        setBusy(false)
      }
    },
    [busy, closeMedia, companyId, connectLiveKit, connectWeb, openChat, t, terminateRemoteCall]
  )

  const rejectIncoming = useCallback(
    async (callId: string) => {
      const call = incomingCallsRef.current.find(item => item.id === callId)
      if (!call || busy || callActionRef.current || !companyId) return
      callActionRef.current = true
      setIncomingCalls(current => current.filter(item => item.id !== call.id))
      setBusy(true)
      try {
        if (call.channel === 'meta') await officialVoiceApi.reject(companyId, call.runId || call.id)
        else {
          await officialVoiceApi.claim(companyId, call.runId || call.id, 'reject')
          await webVoiceApi.action(companyId, call.campaignId, 'reject', call.providerCallId || call.id)
        }
      } catch (error) {
        setIncomingCalls(current => upsertIncoming(current, call))
        toast({ type: 'error', message: error instanceof Error ? error.message : t('translation:voice.rejectFailed') })
      } finally {
        callActionRef.current = false
        setBusy(false)
      }
    },
    [busy, companyId, t]
  )

  const showCallInvitation = useCallback((call: OutboundCall, phone: string) => {
    setPermissionTarget({
      phone,
      campaignId: call.campaignId,
      contactName: normalizedVoiceContactName(call.name, phone) || undefined,
      chatId: call.chatId,
      ownerId: call.ownerId
    })
  }, [])

  const openCallInvitation = useCallback(
    async (call: OutboundCall) => {
      if (busy || activeRef.current) return toast({ type: 'error', message: t('translation:voice.alreadyActive') })
      const phone = digits(call.phone)
      if (!phone) return toast({ type: 'error', message: t('translation:voice.invalidPhone') })
      const campaign = campaigns.find(item => item.id === call.campaignId)
      if (!companyId || !campaign || (await officialCallAvailability(companyId, campaign)) !== 'ready') {
        return toast({ type: 'error', message: t('translation:voice.unavailable') })
      }
      showCallInvitation(call, phone)
    },
    [busy, campaigns, companyId, showCallInvitation, t]
  )

  const persistCallPermission = useCallback(
    async (call: OutboundCall, permission: Awaited<ReturnType<typeof officialVoiceApi.callPermission>>) => {
      if (!call.chatId || !call.ownerId) {
        throw new Error(t('translation:voice.permission.validationSyncFailed'))
      }
      const callPermission = await syncCallPermissionLookup(call.chatId, call.ownerId, permission)
      chatDispatch(updateChatFirebaseAction.success({ chatId: call.chatId, updateChat: { callPermission } }))
      chatDispatch(updateChatFirebaseAction.request({ chatId: call.chatId, updateChat: { callPermission } }))
      return callPermission
    },
    [chatDispatch, t]
  )

  const startOutboundCall = useCallback(
    async (call: OutboundCall) => {
      if (busy || activeRef.current) return toast({ type: 'error', message: t('translation:voice.alreadyActive') })
      const phone = digits(call.phone)
      if (!phone || !companyId) return toast({ type: 'error', message: t('translation:voice.invalidPhone') })
      if (call.channel === 'meta') {
        const campaign = campaigns.find(item => item.id === call.campaignId)
        const availability = campaign && (await officialCallAvailability(companyId, campaign))
        if (availability !== 'ready') {
          return toast({
            type: 'error',
            message: t(
              availability === 'coexistence'
                ? 'translation:voice.settings.coexistenceUnavailable'
                : availability === 'notConfigured'
                ? 'translation:voice.officialNotConfigured'
                : 'translation:voice.unavailable'
            )
          })
        }
      }
      const cachedPermission = effectiveCallPermissionStatus(call.callPermission)
      const mustShowPermissionValidation =
        call.channel === 'meta' && cachedPermission !== 'TEMPORARY' && cachedPermission !== 'PERMANENT'
      if (mustShowPermissionValidation) setPermissionValidation('checking')
      const pendingCall: CallState = {
        ...call,
        id: `pending-${crypto.randomUUID()}`,
        phone,
        name: normalizedVoiceContactName(call.name, phone),
        direction: 'OUTBOUND',
        ticket: callTicket(call),
        startedAt: Date.now(),
        status: 'preparing'
      }
      let outboundOperation: AbortController | null = null
      setBusy(true)
      if (call.channel === 'meta' && !mustShowPermissionValidation) setActiveCall(pendingCall)
      try {
        if (call.channel === 'web') {
          setActiveCall(pendingCall)
          await connectWeb({ ...call, phone }, false)
        } else {
          const { items } = await officialVoiceApi.listConfigs(companyId)
          const config = items.find(
            item => item.provider === 'META_OFFICIAL' && item.active && item.agentId === call.campaignId
          )
          if (!config) throw new Error(t('translation:voice.officialNotConfigured'))
          const permission = await officialVoiceApi.callPermission(companyId, config.id, phone)
          const callPermission = await persistCallPermission(call, permission)
          const permissionStatus = String(permission.status || '')
            .trim()
            .toLowerCase()
          const rawExpiration = Number(permission.expirationTime || 0)
          const expirationTime =
            rawExpiration > 0 && rawExpiration < 1_000_000_000_000 ? rawExpiration * 1000 : rawExpiration
          const confirmedByMeta =
            permissionStatus === 'permanent' || (permissionStatus === 'temporary' && expirationTime > Date.now())
          if (!confirmedByMeta || !permission.canStartCall) {
            setActiveCall(null)
            if (permission.canRequestPermission) {
              setPermissionValidation(null)
              showCallInvitation(call, phone)
            } else {
              setPermissionValidation('denied')
            }
            return
          }
          setPermissionValidation(null)
          outboundOperation = new AbortController()
          outboundOperationRef.current?.abort()
          outboundOperationRef.current = outboundOperation
          locallyTerminatedRunRef.current = null
          setActiveCall({ ...pendingCall, callPermission })
          let lastError: unknown
          const connectionOperationId = crypto.randomUUID()
          for (let attempt = 0; attempt < META_MAX_CONNECT_ATTEMPTS; attempt += 1) {
            if (outboundOperation.signal.aborted) return
            let session: VoiceSession | undefined
            try {
              if (attempt > 0) {
                setActiveCall(current => (current ? { ...current, status: 'preparing' } : current))
                await waitBeforeRetry(attempt)
                if (outboundOperation.signal.aborted) return
              }
              session = await officialVoiceApi.start(companyId, config.id, phone, {
                campaignId: call.campaignId,
                chatId: call.chatId,
                contactName: normalizedVoiceContactName(call.name, phone) || undefined,
                currentStageName: call.stageName,
                ...call.context,
                connection_attempt: attempt + 1,
                connection_attempts_total: META_MAX_CONNECT_ATTEMPTS,
                connection_operation_id: connectionOperationId
              })
              await connectLiveKit(session, { ...call, phone }, true, outboundOperation.signal)
              return
            } catch (error) {
              if (error instanceof OperatorCallCancelledError || outboundOperation.signal.aborted) {
                if (session?.runId && locallyTerminatedRunRef.current !== session.runId) {
                  locallyTerminatedRunRef.current = session.runId
                  await officialVoiceApi
                    .terminate(companyId, session.runId, 'OPERATOR_CANCELLED')
                    .catch(() => undefined)
                }
                await closeMedia()
                return
              }
              lastError = error
              const retryable = error instanceof ProviderConnectionError && error.retryable
              const willRetry = retryable && attempt + 1 < META_MAX_CONNECT_ATTEMPTS
              const finalReason =
                error instanceof ProviderConnectionError && error.reason === 'declined'
                  ? 'REMOTE_REJECTED'
                  : 'NO_ANSWER'
              if (session?.runId) {
                await officialVoiceApi
                  .terminate(companyId, session.runId, willRetry ? 'RETRY_SUPERSEDED' : finalReason)
                  .catch(() => undefined)
              }
              await closeMedia()
              setActiveCall(current => (current ? { ...current, status: 'preparing' } : current))
              if (!willRetry) break
            }
          }
          throw lastError
        }
      } catch (error) {
        setPermissionValidation(null)
        setActiveCall(null)
        await closeMedia()
        toast({ type: 'error', message: error instanceof Error ? error.message : t('translation:voice.startFailed') })
      } finally {
        if (outboundOperationRef.current === outboundOperation) outboundOperationRef.current = null
        setBusy(false)
      }
    },
    [busy, campaigns, closeMedia, companyId, connectLiveKit, connectWeb, persistCallPermission, showCallInvitation, t]
  )

  const requestCallPermission = useCallback(async () => {
    if (!permissionTarget || !selectedPermissionTemplate || busy) return
    const campaign = campaigns.find(item => item.id === permissionTarget.campaignId)
    const chat = linkedChat(permissionTarget, chats)
    if (!campaign || !chat || !campaign.phoneNumberId || !campaign.phoneNumber || !userAuthenticated?.id) {
      toast({ type: 'error', message: t('translation:voice.permission.sendFailed') })
      return
    }

    setBusy(true)
    messagesDispatch(
      createMessageAsync.request({
        typeFile: 'messageTemplate',
        ownerId: campaign.ownerId || companyOwnerId || permissionTarget.ownerId || '',
        campanhaId: campaign.id,
        token: campaign.token,
        isOfficial: true,
        phoneNumberId: campaign.phoneNumberId,
        message: {
          isQuickReply: false,
          typeFile: 'messageTemplate',
          messageTypeSent: selectedPermissionTemplate.typeSent,
          isOfficial: true,
          templateMessageId: selectedPermissionTemplate.messageId,
          campanhaId: campaign.id,
          ownerId: companyOwnerId || campaign.ownerId || permissionTarget.ownerId || '',
          messageText: '',
          phone: permissionTarget.phone,
          senderNumber: permissionTarget.phone,
          receiverNumber: campaign.phoneNumber,
          connectedPhone: campaign.phoneNumber,
          senderId: userAuthenticated.id,
          contactsMessage: [],
          operator: {
            id: userAuthenticated.id,
            name: userAuthenticated.name,
            department: { id: selectedDepartment?.id, name: selectedDepartment?.name }
          },
          chatId: chat.id,
          location: null
        },
        onSuccess: () => {
          const syncInvitation = async () => {
            if (permissionTarget.ownerId) {
              try {
                const callPermission = await markCallPermissionInvitation(chat.id, permissionTarget.ownerId)
                chatDispatch(
                  updateChatFirebaseAction.success({
                    chatId: chat.id,
                    updateChat: { callPermission }
                  })
                )
              } catch {
                toast({ type: 'warning', message: t('translation:voice.permission.statusSyncFailed') })
              }
            }
            setPermissionTarget(null)
            setBusy(false)
            toast({ type: 'success', message: t('translation:voice.permission.sent') })
          }
          void syncInvitation()
        },
        onFailure: () => {
          setBusy(false)
        }
      })
    )
  }, [
    busy,
    campaigns,
    chatDispatch,
    chats,
    companyOwnerId,
    messagesDispatch,
    permissionTarget,
    selectedDepartment?.id,
    selectedDepartment?.name,
    selectedPermissionTemplate,
    t,
    userAuthenticated?.id,
    userAuthenticated?.name
  ])

  useEffect(() => {
    if (!leader || !companyId || !localStorage.getItem('devchat_app_userAuthenticated')) return
    const controller = new AbortController()
    let retry: number | undefined
    let delay = 1_000
    const connect = async () => {
      try {
        await subscribeOperatorCalls(companyId, controller.signal, (type, payload) => {
          delay = 1_000
          if (type === 'snapshot') {
            setIncomingCalls(reconcileIncoming((payload.items || []).map(call => mapIncoming(call, chatsRef.current))))
          } else if (type === 'call.ringing' && payload.call) {
            setIncomingCalls(current => upsertIncoming(current, mapIncoming(payload.call, chatsRef.current)))
          } else if ((type === 'call.claimed' || type === 'call.ended') && payload.call) {
            if (type === 'call.ended') window.dispatchEvent(new CustomEvent('devzapp:voice-history-changed'))
            const action = payload.call.claimAction || (payload.call.state === 'REJECTING' ? 'reject' : 'accept')
            const actor = payload.call.claimedBy || payload.call.acceptedBy
            if (type === 'call.claimed') operatorClaimsRef.current.set(payload.call.id, { action, actor })
            const wasQueued = incomingCallsRef.current.some(call => call.id === payload.call?.id)
            setIncomingCalls(current => current.filter(call => call.id !== payload.call?.id))
            if (wasQueued && type === 'call.claimed' && actor?.name) {
              toast({
                type: 'info',
                message:
                  action === 'reject'
                    ? t('translation:voice.rejectedBy', '{{name}} recusou a ligação.', { name: actor.name })
                    : t('translation:voice.acceptedBy', '{{name}} aceitou a ligação.', { name: actor.name })
              })
            }
            if (wasQueued && type === 'call.ended' && payload.call.outcome === 'OPERATOR_REJECTED' && actor?.name) {
              toast({
                type: 'info',
                message: t('translation:voice.rejectedBy', '{{name}} recusou a ligação.', { name: actor.name })
              })
            }
            const retryAttemptEnded = payload.call.outcome === 'ESTABLISHMENT_FAILED'
            if (type === 'call.ended' && activeRef.current?.runId === payload.call.id && !retryAttemptEnded) {
              setActiveCall(null)
              closeMedia().catch(() => undefined)
            }
          } else if (type === 'call.active' && payload.call) {
            const claim =
              operatorClaimsRef.current.get(payload.call.id) ||
              ({
                action: payload.call.claimAction || 'accept',
                actor: payload.call.claimedBy || payload.call.acceptedBy
              } as const)
            operatorClaimsRef.current.delete(payload.call.id)
            if (claim?.action === 'accept' && claim.actor?.name) {
              toast({
                type: 'info',
                message: t('translation:voice.answeredBy', 'Chamada atendida por {{name}}.', {
                  name: claim.actor.name
                })
              })
            }
            setIncomingCalls(current => current.filter(call => call.id !== payload.call?.id))
            setActiveCall(current =>
              current?.runId === payload.call?.id
                ? { ...current, status: 'connected', connectedAt: Date.now() }
                : current
            )
          }
        })
      } catch {
        // Reconnect with a bounded delay; every stream starts with a snapshot.
      }
      if (!controller.signal.aborted) {
        retry = window.setTimeout(connect, delay)
        delay = Math.min(delay * 2, 15_000)
      }
    }
    connect().catch(() => undefined)
    return () => {
      controller.abort()
      if (retry) window.clearTimeout(retry)
    }
  }, [closeMedia, companyId, leader, t])

  const toggleMute = useCallback(async () => {
    const next = !muted
    if (roomRef.current) await roomRef.current.localParticipant.setMicrophoneEnabled(!next)
    microphoneRef.current?.getAudioTracks().forEach(track => (track.enabled = !next))
    setMuted(next)
  }, [muted])

  const openActiveChat = useCallback(() => {
    if (activeRef.current) void openChat(activeRef.current)
  }, [openChat])

  const openWaitingChat = useCallback(
    (callId: string) => {
      const call = incomingCallsRef.current.find(item => item.id === callId)
      if (call) void openChat(call)
    },
    [openChat]
  )

  const value = useMemo(
    () => ({
      activeCall: activeCallView,
      waitingCalls: waitingCallViews,
      busy,
      elapsed,
      muted,
      speaking,
      remoteAudioLevel,
      startOutboundCall,
      openCallInvitation,
      hangup,
      toggleMute,
      openActiveChat,
      acceptWaitingCall: acceptIncoming,
      rejectWaitingCall: rejectIncoming,
      openWaitingChat
    }),
    [
      activeCallView,
      acceptIncoming,
      busy,
      elapsed,
      hangup,
      muted,
      openActiveChat,
      openCallInvitation,
      openWaitingChat,
      rejectIncoming,
      remoteAudioLevel,
      speaking,
      startOutboundCall,
      toggleMute,
      waitingCallViews
    ]
  )
  return (
    <VoiceContext.Provider value={value}>
      {children}
      {showSoundPrompt && <VoiceSoundPrompt onActivate={activateSound} onDismiss={dismissSoundPrompt} />}
      {incomingView && (
        <IncomingCallCard
          call={incomingView}
          ringtoneError={ringtoneError}
          onRetryRingtone={retryRingtone}
          waitingCalls={waitingCallViews}
          busy={busy}
          onAccept={() => {
            acceptIncoming(incomingView.id).catch(() => undefined)
          }}
          onReject={() => {
            rejectIncoming(incomingView.id).catch(() => undefined)
          }}
          onOpenChat={
            incomingView.chatIsOpen
              ? undefined
              : () => {
                  void openChat(incomingView)
                }
          }
          onAcceptWaiting={callId => {
            void acceptIncoming(callId)
          }}
          onRejectWaiting={callId => {
            void rejectIncoming(callId)
          }}
          onOpenWaitingChat={openWaitingChat}
        />
      )}
      {activeCallView && (
        <ActiveCallCard
          call={activeCallView}
          waitingCalls={waitingCallViews}
          busy={busy}
          elapsed={elapsed}
          muted={muted}
          speaking={speaking}
          audioLevel={remoteAudioLevel}
          callStatus={activeCallView.status}
          onMute={() => {
            toggleMute().catch(() => undefined)
          }}
          onHangup={() => {
            hangup().catch(() => undefined)
          }}
          onOpenChat={
            activeCallView.chatIsOpen
              ? undefined
              : () => {
                  void openChat(activeCallView)
                }
          }
          onAcceptWaiting={callId => {
            void acceptIncoming(callId)
          }}
          onRejectWaiting={callId => {
            void rejectIncoming(callId)
          }}
          onOpenWaitingChat={openWaitingChat}
        />
      )}
      <CallPermissionModal
        isOpen={Boolean(permissionTarget)}
        templates={permissionTemplates}
        selectedTemplateId={permissionTemplateId}
        search={permissionTemplateSearch}
        contactName={permissionTarget?.contactName || permissionTarget?.phone || ''}
        loading={isLoadingOnFetchMessageTemplates}
        submitting={busy}
        onSearchChange={setPermissionTemplateSearch}
        onSelectTemplate={setPermissionTemplateId}
        onClose={() => {
          if (!busy) setPermissionTarget(null)
        }}
        onSubmit={() => {
          requestCallPermission().catch(() => undefined)
        }}
      />
      <CallPermissionValidationModal
        isOpen={permissionValidation !== null}
        denied={permissionValidation === 'denied'}
        onClose={() => setPermissionValidation(null)}
      />
    </VoiceContext.Provider>
  )
}

export function useVoiceCall() {
  const value = useContext(VoiceContext)
  if (!value) throw new Error('useVoiceCall must be used inside VoiceCallProvider')
  return value
}

export function campaignVoiceChannel(campaign?: Campaign): VoiceChannel {
  return campaign?.isOfficial ? 'meta' : 'web'
}
